import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FilterBar, FilterSearch, FilterSelect } from "@/components/data-display/filter-bar";
import { StatCard } from "@/components/data-display/stat-card";
import { Table, TableWrapper } from "@/components/ui/table";
import { TableState } from "@/components/data-display/table-state";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";

/**
 * Evidence Center (`/evidence`) — WIP layout port of the designer's
 * `features/oversight/evidence.tsx` (`EvidenceOverview`).
 *
 * No backend publishes sealed evidence packets in this environment (ALL-5709 /
 * ALL-5778: the authored fixture packets were removed), so this is the designer's
 * structure — header, six stat tiles, filter row, packet table — with every value
 * rendered as the shared unknown marker and the table body a designed empty state.
 * No packet id, subject, actor, timestamp or count is invented. The filter group
 * is a natively disabled fieldset and carries WIP_ACTION_HINT; the two links that
 * stay point at routes that exist in App.tsx and are live today.
 */

/** A value the backend does not carry — the same marker StatCard falls back to. */
const UNKNOWN = "—";

/** The six tiles the designer's overview shows. Labels only — never the fixture counts. */
const STAT_LABELS = ["Total Packets", "Transfer", "Approval", "Recovery", "Admin", "Screening"];

/** The designer's seven packet columns. */
const COLUMNS = ["Packet ID", "Type", "Subject", "Created (UTC)", "Actor", "Status", "Action"];

/** Id of the hidden node carrying WIP_ACTION_HINT for the disabled filter group. */
const WIP_ACTIONS_ID = "evidence-wip-actions";

/**
 * The filter row sits inside a `disabled` fieldset, so nothing in it can fire:
 * FilterSearch and FilterSelect are controlled components and require a change
 * handler, and this is the one they are given. The HTML `disabled` on the group —
 * not this function — is what makes the row non-interactive, so the row cannot
 * pretend to filter against a backend that does not exist.
 */
const NO_FILTER_CHANGE = () => undefined;

export default function EvidencePage() {
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">Evidence Center</h1>
            <WipHint />
          </div>
          <p className="mt-1 text-sm text-muted">
            Evidence packets for transfers, approvals and administrative actions.
          </p>
        </div>
      </div>
      <span id={WIP_ACTIONS_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </span>
      {/* One plain sentence: this area has no backend here, so nothing below is a real record. */}
      <p className="mb-5 text-sm text-muted">
        Evidence has no backend in this environment yet, so no packet listed here reflects a real sealed record.
      </p>

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {STAT_LABELS.map((label) => (
          <StatCard key={label} value={UNKNOWN} label={label} />
        ))}
      </div>

      <fieldset disabled className="contents" title={WIP_ACTION_HINT} aria-describedby={WIP_ACTIONS_ID}>
        <legend className="sr-only">Evidence packet filters</legend>
        <FilterBar>
          <FilterSearch
            value=""
            onChange={NO_FILTER_CHANGE}
            placeholder="Search packet, subject or actor"
            ariaLabel="Search evidence packets"
          />
          <FilterSelect
            value=""
            onChange={NO_FILTER_CHANGE}
            ariaLabel="Filter by packet type"
            options={[{ value: "", label: "All Types" }]}
          />
          <FilterSelect
            value=""
            onChange={NO_FILTER_CHANGE}
            ariaLabel="Filter by actor"
            options={[{ value: "", label: "All Actors" }]}
          />
          <FilterSelect
            value=""
            onChange={NO_FILTER_CHANGE}
            ariaLabel="Filter by date range"
            options={[{ value: "", label: "Any Date" }]}
          />
          <Button variant="secondary" size="sm" type="button">
            Search
          </Button>
        </FilterBar>
      </fieldset>

      <TableWrapper>
        <Table>
          <thead>
            <tr className="border-b border-line bg-bg text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
              {COLUMNS.map((column) => (
                <th key={column} className="px-4 py-2.5">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <TableState
              variant="empty"
              colSpan={COLUMNS.length}
              title="No evidence packets"
              description="No evidence service publishes packets in this environment yet, so there are no sealed records to list."
            />
          </tbody>
        </Table>
      </TableWrapper>

      <section aria-labelledby="evidence-available-heading" className="mx-auto mt-6 max-w-[520px]">
        <h2 id="evidence-available-heading" className="mb-2 text-sm font-bold text-ink">
          What&apos;s available now
        </h2>
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          <li>
            <Link to="/admin/audit-log" className="block px-4 py-3 hover:bg-bg">
              <span className="text-sm font-semibold text-primary">Audit Trail</span>
              <span className="block text-xs text-muted">
                The live trail of administrative actions, which is available today.
              </span>
            </Link>
          </li>
          <li>
            <Link to="/admin/trace-lookup" className="block px-4 py-3 hover:bg-bg">
              <span className="text-sm font-semibold text-primary">Trace lookup</span>
              <span className="block text-xs text-muted">
                Look up a request by correlation id, which is available today.
              </span>
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
